import { getElementScale } from '../calculator/popups/element-scale'

const lastAnchors = new WeakMap<Element, Element>()
const sceneOffsets = new WeakMap<HTMLElement, number>()

export const getDemoVisibleBounds = (element: Element) => {
  const player = element.closest('.demo-player')
  const content = player?.querySelector('.demo__content')
  if (!player || !content) return null

  const viewport = window.visualViewport
  let top = viewport?.offsetTop || 0
  let bottom = top + (viewport?.height || window.innerHeight)
  const layer = element.closest('.demo-player__modal-layer') || content
  let parent: Element | null = layer

  while (parent && parent !== document.body) {
    const style = getComputedStyle(parent)
    if (parent === layer || /hidden|clip|auto|scroll/.test(style.overflowY)) {
      const bounds = parent.getBoundingClientRect()
      top = Math.max(top, bounds.top)
      bottom = Math.min(bottom, bounds.bottom)
    }
    parent = parent.parentElement
  }

  const toolbar = player.querySelector('.demo-player__toolbar')?.getBoundingClientRect()
  if (toolbar && toolbar.top < bottom && toolbar.bottom > top) {
    bottom = Math.max(top, toolbar.top)
  }

  return bottom > top ? {
    top,
    bottom,
    center: (top + bottom) / 2,
  } : null
}

const getDemoCenteringScene = (element: Element) => {
  const player = element.closest('.demo-player')
  const anchor = player && lastAnchors.get(player)
  const inContent = element.closest('.demo__content')
  const modal = element.closest<HTMLElement>('.MuiDialog-paper') ||
    (!inContent && anchor?.isConnected ? anchor.closest<HTMLElement>('.MuiDialog-paper') : null)
  return modal || player?.querySelector<HTMLElement>('.demo-calculator-canvas')
}

export const shouldCenterDemoTarget = (element: Element) => {
  const scene = getDemoCenteringScene(element)
  const player = element.closest('.demo-player')
  const layer = element.closest('.demo-player__modal-layer') || player?.querySelector('.demo__content')
  if (!scene || !layer) return false

  const bounds = scene.getBoundingClientRect()
  const available = layer.getBoundingClientRect()
  const toolbar = player?.querySelector('.demo-player__toolbar')?.getBoundingClientRect()
  const bottom = toolbar && toolbar.top < available.bottom && toolbar.bottom > available.top
    ? Math.max(available.top, toolbar.top)
    : available.bottom
  const scale = getElementScale(scene)
  const sceneHeight = Math.max(bounds.height, scene.scrollHeight * scale)

  // Page scrolling changes viewport clipping, but must not switch a fitting scene's camera mode.
  return sceneHeight + 24 >= bottom - available.top
}

const notifyDemoSceneMovement = (scene: HTMLElement) => {
  scene.closest('.demo-player')?.querySelector('.demo__content')?.dispatchEvent(new Event('scroll'))
  window.dispatchEvent(new Event('scroll'))
}

const resetDemoScene = (scene: HTMLElement) => {
  const wasShifted = sceneOffsets.has(scene) || Boolean(scene.style.translate)
  scene.style.removeProperty('translate')
  sceneOffsets.delete(scene)
  if (wasShifted) notifyDemoSceneMovement(scene)
}

export const restoreDemoNaturalLayout = (element: Element) => {
  const scene = getDemoCenteringScene(element)
  if (!scene || shouldCenterDemoTarget(element)) return

  resetDemoScene(scene)
  const content = scene.closest<HTMLElement>('.demo__content')
  if (content) content.scrollTop = 0
}

export const getDemoScrollContainers = (element: Element) => {
  const player = element.closest('.demo-player')
  if (!player) return []

  const content = player.querySelector<HTMLElement>('.demo__content')
  const modal = element.closest('.MuiDialog-root')
  const inContent = content?.contains(element)
  if (inContent || modal) lastAnchors.set(player, element)
  const anchor = inContent || modal ? element : lastAnchors.get(player) || element
  restoreDemoNaturalLayout(element)
  const containers: HTMLElement[] = []
  let parent = anchor.parentElement

  while (parent && parent !== player) {
    const style = getComputedStyle(parent)
    if (parent === content || (/auto|scroll/.test(style.overflowY) && parent.scrollHeight > parent.clientHeight)) {
      parent.style.setProperty('overflow-anchor', 'none')
      containers.push(parent)
    }
    parent = parent.parentElement
  }

  return containers
}

export const getDemoScrollDestination = ({ element, content }: DemoScrollDestinationParams) => {
  const shouldCenter = shouldCenterDemoTarget(element)
  if (!shouldCenter && content.matches('.demo__content')) return 0
  const visible = getDemoVisibleBounds(element)
  if (!visible) return content.scrollTop
  const target = element.getBoundingClientRect()
  const scale = getElementScale(content)
  const bounds = content.getBoundingClientRect()
  const top = Math.max(visible.top, bounds.top)
  const bottom = Math.min(visible.bottom, bounds.bottom)
  const center = bottom > top ? (top + bottom) / 2 : visible.center
  const nearestOffset = target.top < top
    ? target.top - top
    : Math.max(0, target.bottom - bottom)
  const offset = (shouldCenter
    ? target.top + target.height / 2 - center
    : nearestOffset) / scale
  return Math.max(0, Math.min(content.scrollHeight - content.clientHeight, content.scrollTop + offset))
}

export const finishDemoCentering = (element: Element) => {
  const visible = getDemoVisibleBounds(element)
  if (!visible) return false
  if (!shouldCenterDemoTarget(element)) return true
  const bounds = element.getBoundingClientRect()
  const offset = visible.center - bounds.top - bounds.height / 2
  if (Math.abs(offset) <= 1) return true

  const scene = getDemoCenteringScene(element)
  if (!scene) return false

  const nextOffset = (sceneOffsets.get(scene) || 0) + offset / getElementScale(scene)
  sceneOffsets.set(scene, nextOffset)
  scene.style.translate = `0 ${nextOffset}px`
  // Anchored popups measure their reference again after the scene moves.
  notifyDemoSceneMovement(scene)
  const result = element.getBoundingClientRect()
  return Math.abs(result.top + result.height / 2 - visible.center) <= 1
}

export const resetDemoCentering = (content: Element) => {
  const player = content.closest('.demo-player')
  if (player) lastAnchors.delete(player)
  const canvas = content.querySelector<HTMLElement>('.demo-calculator-canvas')
  if (canvas) {
    resetDemoScene(canvas)
  }
}
